import React, { useState } from 'react';
import { Send, Mail, Building, User, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        organization: formData.organization,
        role: formData.role,
        message: formData.message || null
      });
      
      setIsSubmitted(true);
      toast.success('Thank you! We\'ll be in touch within 24 hours.');
      
      // Reset after delay
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          role: '',
          message: ''
        });
      }, 5000);
    } catch (error) {
      console.error('Contact submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[var(--bg-section)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--niko-teal)] text-sm font-medium mb-4">
              Get Started
            </span>
            <h2 className="heading-2 mb-6">
              Ready to Transform{' '}
              <span className="text-[var(--niko-teal)]">Patient Safety</span>?
            </h2>
            <p className="body-large mb-8">
              Schedule a personalized demo and see how NikoSafe Connect can streamline 
              your infection prevention, quality, and patient safety operations.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-wash)] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[var(--niko-teal)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-primary)]">Email Us</p>
                  <p className="text-[var(--text-secondary)]">info@nikosafeconnect.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--accent-wash)] flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[var(--niko-teal)]" />
                </div>
                <div>
                  <p className="font-medium text-[var(--text-primary)]">Call Us</p>
                  <p className="text-[var(--text-secondary)]">(555) 123-4567</p>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-10 pt-10 border-t border-[var(--border-light)]">
              <p className="text-sm text-[var(--text-muted)] mb-4">Trusted by healthcare organizations</p>
              <div className="flex items-center gap-6 opacity-50">
                <span className="text-sm font-medium text-[var(--text-secondary)]">HIPAA Compliant</span>
                <span className="text-sm font-medium text-[var(--text-secondary)]">SOC 2 Type II</span>
                <span className="text-sm font-medium text-[var(--text-secondary)]">ISO 27001</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-[var(--border-light)] p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-[var(--niko-green)]/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[var(--niko-green)]" />
                </div>
                <h3 className="heading-3 mb-2">Thank You!</h3>
                <p className="text-[var(--text-secondary)]">
                  We'll be in touch within 24 hours to schedule your demo.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="w-4 h-4 text-[var(--text-muted)]" />
                      Full Name
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      required
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[var(--text-muted)]" />
                      Work Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jane@hospital.org"
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[var(--text-muted)]" />
                      Phone
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 123-4567"
                      className="h-12"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="organization" className="flex items-center gap-2">
                      <Building className="w-4 h-4 text-[var(--text-muted)]" />
                      Organization
                    </Label>
                    <Input
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      placeholder="Hospital or Facility Name"
                      required
                      className="h-12"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Your Role</Label>
                  <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="w-full h-12 px-4 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[var(--niko-teal)] focus:border-transparent"
                  >
                    <option value="">Select your role</option>
                    <option value="infection-prevention">Infection Preventionist</option>
                    <option value="quality-director">Quality Director</option>
                    <option value="patient-safety">Patient Safety Officer</option>
                    <option value="nursing-leadership">Nursing Leadership</option>
                    <option value="cno-cmo">CNO / CMO</option>
                    <option value="executive">Executive / Administrator</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-[var(--text-muted)]" />
                    How can we help?
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your current challenges or what you'd like to see in a demo..."
                    rows={4}
                    className="resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary h-12 text-base"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Request Demo
                      <Send className="w-5 h-5" />
                    </span>
                  )}
                </Button>

                <p className="text-xs text-center text-[var(--text-muted)]">
                  By submitting, you agree to receive communications from NikoSafe Connect.
                  We respect your privacy and will never share your information.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
